/**
 * Appearance state: theme (system | light | dark | black), skin (studio | paper
 * | console) and photo treatment (cover | whole). The choice is written to
 * <html data-theme data-skin data-photo> so tokens.css applies it, and
 * remembered per device in localStorage.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

export type ThemePreference = 'system' | 'light' | 'dark' | 'black'
export type ResolvedTheme = 'light' | 'dark' | 'black'
export type Skin = 'studio' | 'paper' | 'console'
export type PhotoMode = 'cover' | 'whole'

export const THEMES: Array<{ id: ThemePreference; label: string; note: string }> = [
  { id: 'system', label: 'System', note: 'Follows the operating system between Light and Dark.' },
  { id: 'light', label: 'Light', note: 'Paper-white surfaces, dark ink.' },
  { id: 'dark', label: 'Dark', note: 'Slate canvas and cards.' },
  { id: 'black', label: 'Black', note: 'For OLED panels: pure black canvas, hairlines instead of raised surfaces.' },
]
export const SKINS: Array<{ id: Skin; label: string; note: string; swatch: string; accent: string }> = [
  { id: 'studio', label: 'Studio', note: 'Emerald on slate, 12 px corners, body numerals. The default.', swatch: '#131B2B', accent: '#10B981' },
  { id: 'paper', label: 'Paper', note: 'Warm neutrals, brick accent, softer corners, type a touch larger.', swatch: '#242019', accent: '#b5452e' },
  { id: 'console', label: 'Console', note: 'Cool neutrals, cobalt accent, 2 px corners, monospace numerals.', swatch: '#10161e', accent: '#7ea6f0' },
]

interface ThemeContextValue {
  theme: ThemePreference
  resolved: ResolvedTheme
  skin: Skin
  photo: PhotoMode
  setTheme: (theme: ThemePreference) => void
  setSkin: (skin: Skin) => void
  setPhoto: (mode: PhotoMode) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const KEY_THEME = 'propalpha.theme'
const KEY_SKIN = 'propalpha.skin'
const KEY_PHOTO = 'propalpha.photo'

function read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = window.localStorage.getItem(key)
    return value && (allowed as readonly string[]).includes(value) ? (value as T) : fallback
  } catch { return fallback }
}
function write(key: string, value: string): void {
  try { window.localStorage.setItem(key, value) } catch { /* storage may be unavailable */ }
}
function systemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(() => read(KEY_THEME, ['system', 'light', 'dark', 'black'] as const, 'system'))
  const [skin, setSkinState] = useState<Skin>(() => read(KEY_SKIN, ['studio', 'paper', 'console'] as const, 'studio'))
  const [photo, setPhotoState] = useState<PhotoMode>(() => read(KEY_PHOTO, ['cover', 'whole'] as const, 'cover'))
  const [system, setSystem] = useState<'light' | 'dark'>(() => systemTheme())

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: light)')
    const listener = () => setSystem(systemTheme())
    media.addEventListener('change', listener)
    return () => media.removeEventListener('change', listener)
  }, [])

  const resolved: ResolvedTheme = theme === 'system' ? system : theme

  useEffect(() => {
    const root = document.documentElement
    root.setAttribute('data-theme', resolved)
    root.setAttribute('data-theme-preference', theme)
    root.setAttribute('data-skin', skin)
    if (photo === 'whole') root.setAttribute('data-photo', 'whole'); else root.removeAttribute('data-photo')
  }, [resolved, theme, skin, photo])

  const setTheme = useCallback((next: ThemePreference) => { setThemeState(next); write(KEY_THEME, next) }, [])
  const setSkin = useCallback((next: Skin) => { setSkinState(next); write(KEY_SKIN, next) }, [])
  const setPhoto = useCallback((next: PhotoMode) => { setPhotoState(next); write(KEY_PHOTO, next) }, [])

  const value = useMemo(() => ({ theme, resolved, skin, photo, setTheme, setSkin, setPhoto }), [theme, resolved, skin, photo, setTheme, setSkin, setPhoto])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext)
  if (!value) throw new Error('useTheme must be used inside ThemeProvider')
  return value
}
